<template>
  <div
    class="cart-item-wrapper"
    :data-productKey="product.code">
    <div
      class="cart-item-wrapper__cart-item cart-item"
      :class="{'cart-item--small': isSmallCartItem}"
    >
      <div class="cart-item__left-wrapper">
        <cart-item-remove
          v-if="isAllowCartManagement"
          :product-data="product"
          :is-in-wish-list="isInWishList"
          @clickWishlist="addToWishList"
          @clickRemove="removeFromCart"
          class="cart-item__remove-wrapper"
        ></cart-item-remove>
        <a
          class="cart-item__img-wrapper"
          :href="product.productUrl">
          <img
            class="cart-item__img"
            :src="product.imageUrl"
            :alt="product.name"
            @error="e => e.target.src = '/img/no-image.png'"
          />
        </a>
      </div>
      <div class="cart-item__right-wrapper">
        <div class="cart-item__title-wrapper">
          <a
            :href="product.productUrl"
            class="cart-item__title link">
            {{ product.name }}
          </a>
        </div>
        <div class="cart-item__delivery-status" v-if="product.delivery_status && (product.delivery_status.fm_only || product.delivery_status.im_only)">
          <span v-if="product.delivery_status.fm_only">{{ $t('delivery-status__fm') }}</span>
          <span v-if="product.delivery_status.im_only">{{ $t('delivery-status__im') }}</span>
        </div>
        <buy-btn
          v-if="isClientCart"
          :product-id="product.id"
          :code="product.code"
          :price="product.price"
          :btn-text="$t('add_to_cart')"
          class="cart-item__add-to-cart"
        ></buy-btn>
        <span
          v-if="isCheckoutStatusShown"
          class="cart-item__status"
          :class="['cart-item__status--' + computedProductStatus.statusSlug]"
        >
          {{ computedProductStatus.statusText }}
        </span>
        <div
          v-if="bonusesDebitingInfo && product.isAwardedBonuses"
          class="cart-item__bonuses-wrapper">
          <img
            class="cart-item__bonuses-icon"
            src="/img/svgo/product-page/bonus_icon.svg"
            alt="bonuses-icon" />
          <span class="cart-item__bonuses-text">+{{ bonusesDebitingInfo | CurrencyFormatter }} {{ [$t('bonus'), $t('bonus-2'), $t('bonus-3')] | Pluralize(bonusesDebitingInfo) }}</span>
        </div>
        <div class="cart-item__actions-wrapper">
          <div class="cart-item__price-wrapper">
            <span
              v-if="product.price || product.price_old"
              class="cart-item__price">
              {{ product.price_old || product.price | currencyFormatter }} {{ product.currency }}
            </span>
            <!-- price_old для корзины, price выводим для фикса в окне бронирования -->
          </div>
          <div
            class="cart-item__counter-wrapper"
            v-if="product.maxQuantity">
            <cart-item-counter
              :product-quantity="product.itemQuantity"
              :product-max-quantity="product.maxQuantity"
              :size="isSmallCartItem ? 'small' : 'regular'"
              @changeQuantity="changeQuantity"
              @decrement="removeFromCartGA4"
              @increment="addToCartGA4"
            ></cart-item-counter>
          </div>
          <div
            class="cart-item__total-price-wrapper"
            v-if="product.itemQuantity && product.price"
          >
            <del
              class="cart-item__price cart-item__price--old"
              v-if="isShowOldPrice">
              {{ computedTotalOldPrice | currencyFormatter }} {{ product.currency }}
            </del>
            <span
              class="cart-item__total-price"
              :class="{'cart-item__total-price--contrast': isShowOldPrice}">
              {{ computedTotalPrice | currencyFormatter }} {{ product.currency }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="product.items && showInnerItems"
      class="cart-item-wrapper__inner-items cart-inner-items">
      <span
        v-if="product.type === 'gift_set'"
        class="cart-inner-items__title">{{ $t('gift_title') }}</span>
      <ul class="cart-inner-items__list">
        <li
          class="cart-inner-items__item"
          v-for="(itemProduct, i) in product.items"
          :key="i">
          <a
            class="cart-inner-items__item-img-wrapper"
            :href="itemProduct.productUrl">
            <img
              class="cart-inner-items__item-img"
              :src="itemProduct.imageUrl"
              :alt="itemProduct.name"
              @error="e => e.target.src = '/img/no-image.png'"
            />
          </a>
          <a
            :href="itemProduct.productUrl"
            class="cart-inner-items__item-title link">
            {{ itemProduct.name }}
          </a>
        </li>
      </ul>
    </div>
    <loader :is-loading="isLoading"></loader>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import CurrencyFormatter from '@/filters/CurrencyFormatter'
import Pluralize from '@/filters/Pluralize'
import { GoogleEnhancedEcommerce, GoogleTagManagerService } from '@/services/AnalyticService'
import CartItemCounter from '@/components/cart/cart-item/CartItemCounter'
import CartItemRemove from '@/components/cart/cart-item/CartItemRemove'
import BuyBtn from '@/components/cart/BuyBtn'
import Loader from '@/components/helpers/Loader'

export default {
  name: 'CartItem',
  data () {
    return {
      approveRemove: false,
      isLoading: false
    }
  },
  components: { CartItemRemove, CartItemCounter, Loader, BuyBtn },
  props: {
    product: {
      type: Object,
      required: true
    },
    isSmallCartItem: Boolean,
    showProductDeliveryStatus: Boolean,
    isReserve: Boolean,
    isClientCart: Boolean,
    showInnerItems: Boolean,
    isAllowCartManagement: {
      type: Boolean,
      default: true

    }
  },
  computed: {
    ...mapGetters(['urlLanguagePrefix']),
    ...mapGetters('wishlist', ['productsIdsInWishlist']),
    ...mapState({
      config: state => state.init.config,
      user: state => state.user,
      formData: state => state.checkout.formData,
      bonusesInfo: state => state.bonuses.bonusesInfo,
      bonusValue: state => state.bonuses.bonusesInfoCart.bonus_debiting,
      useBonuses: state => state.checkoutNew.formData.useBonuses,
      deliveryMethod: state => state.deliveryNew.formData.deliveryMethod,
      selectedSpot: state => state.deliveryNew.formData.selectedSpot,
      spentBonuses: state => state.checkoutNew.formData.spentBonuses,
      deliveryCity: state => state.deliveryNew.formData.deliveryCity
    }),
    computedCode () {
      return this.product.code
    },
    bonusesPercent () {
      return this.bonusesInfo ? this.bonusesInfo.current_rate : 5
    },
    bonusesDebitingInfo () {
      if (this.product.bonus) {
        return this.product.bonus
      }
      // if (!this.product.isSale && this.product.isDebitingBonuses && this.bonusesPercent && !this.config.happy_hours) {
      //   return Math.round((this.product.price * this.bonusesPercent) / 100 * this.product.itemQuantity)
      // }

      return null
    },
    isShowOldPrice () {
      return this.product.price_old && this.product.price_old > this.product.price
    },
    computedProductStatus () {
      const result = {
        statusText: '',
        statusSlug: ''
      }

      switch (this.product.status) {
        case 0:
          result.statusText = this.$t('reserve_order_not_available')
          result.statusSlug = 'not-in-stock'
          break
        case 1:
          result.statusText = this.$t('reserve_now_in_stock')
          result.statusSlug = 'in-stock'
          break
        case 2:
          result.statusText = this.$t('reserve_will_be_in_stock')
          result.statusSlug = 'will-be-in-stock'
          break
      }

      return result
    },
    computedTotalOldPrice () {
      return Math.round(this.product.price_old * this.product.itemQuantity)
    },
    computedTotalPrice () {
      return Math.round(this.product.price * this.product.itemQuantity)
    },
    storeUidForCheckout () {
      return this.formData?.deliveryType?.methodId === 1 &&
      this.formData?.deliveryInfo?.deliverySpot?.uid
        ? this.formData.deliveryInfo.deliverySpot.uid
        : null
    },
    isDeliveryType () {
      return (
        this.formData?.deliveryType?.methodId === 1 &&
        this.formData?.deliveryInfo?.deliverySpot
      )
    },
    isCheckoutStatusShown () {
      return (this.product.status || this.product.status === 0) && this.showProductDeliveryStatus && this.isDeliveryType
    },
    isInWishList () {
      return this.productsIdsInWishlist.length ? this.productsIdsInWishlist.includes(this.product.id) : false
    },
    analyticsData () {
      return this.product['analytics_data'].map(item => {
        item.quantity *= this.product.itemQuantity
        return item
      })
    }
  },
  methods: {
    ...mapActions([
      'REMOVE_PRODUCT_FROM_CART',
      'CHANGE_PRODUCT_QUANTITY',
      'CHANGE_CART_RTB_HOUSE_CODE_FLAG',
      'GET_FULL_CART'
    ]),
    ...mapActions('deliveryNew', [
      'FETCH_DELIVERY_TYPES'
    ]),
    ...mapActions('wishlist', ['CHANGE_WISH_LIST_STATUS']),
    sendBonusesToABM () {
      let cartParams = {
        notUpdateCheckout: true,
        deliveryType: this.deliveryMethod?.method_id || '',
        storeUid: this.selectedSpot?.uid || '',
        bonuses: this.useBonuses ? this.spentBonuses : 0
      }
      this.GET_FULL_CART(cartParams)
    },
    addToWishList () {
      if (this.user && this.user.auth) {
        this.isLoading = true
        this.CHANGE_WISH_LIST_STATUS({
          id: this.product.id,
          code: this.product.code || '',
          price: this.product.price || '',
          isInStock: this.product.status === 1
        })
          .then(() => {
            if (GoogleEnhancedEcommerce.isEnabled) {
              GoogleEnhancedEcommerce.addToWishlist({
                value: +GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.product['analytics_data'][0]).price * parseInt(GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.product['analytics_data'][0]).quantity || 1),
                universal_value: this.isInWishList ? 'add' : 'removed',
                items: [GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(this.product['analytics_data'][0])]
              })
            }
            this.removeFromCart()
          })
          .catch(error => this.notifyError(error))
      } else {
        window.addEventListener('wishListTrigger', () => {
          this.addToWishList()
        })

        this.$modal.show('auth-modal', {
          title: this.$t('wishlist.auth'),
          isAjaxAuth: true,
          referrer: 'wish-list'
        })
      }
    },
    removeFromCartGA4 ({ curr, prev }) {
      if (GoogleEnhancedEcommerce.isEnabled) {
        const dataObj = { ...this.analyticsData[0], quantity: Math.abs(curr - prev) }
        GoogleEnhancedEcommerce.removeFromCart({
          value: +GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(dataObj).price * parseInt(GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(dataObj).quantity),
          items: [GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(dataObj)]
        })
      } else {
        GoogleTagManagerService.pushToAnalytics({
          analyticsData: this.analyticsData.map(el => {
            return { ...el, quantity: 1 }
          }),
          eventName: 'remove_from_cart'
        })
      }
    },
    addToCartGA4 ({ curr, prev }) {
      if (GoogleEnhancedEcommerce.isEnabled) {
        const dataObj = { ...this.analyticsData[0], quantity: Math.abs(curr - prev) }
        GoogleEnhancedEcommerce.addToCart({
          value: +GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(dataObj).price * parseInt(GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(dataObj).quantity),
          items: [GoogleEnhancedEcommerce.getGoogleEnhancedEcommerceData(dataObj)]
        })
      } else {
        GoogleTagManagerService.pushToAnalytics({
          analyticsData: this.analyticsData.map(el => {
            return { ...el, quantity: 1 }
          }),
          eventName: 'add_to_cart'
        })
      }
    },
    removeFromCart () {
      this.isLoading = true
      this.REMOVE_PRODUCT_FROM_CART({
        ...this.product,
        storeUid: this.storeUidForCheckout,
        deliveryType: this.formData.deliveryType ? this.formData.deliveryType.methodId : null,
        bonuses: this.bonusValue
      })
        .then(() => {
          this.removeFromCartGA4({ curr: 0, prev: this.analyticsData[0].quantity })

          this.isLoading = false
          this.CHANGE_CART_RTB_HOUSE_CODE_FLAG(true)
          this.$emit('changedCart')
          if (this.showProductDeliveryStatus) {
            if (this.useBonuses) {
              this.sendBonusesToABM()
            }
            if (this.deliveryCity.id) {
              this.FETCH_DELIVERY_TYPES()
            }
          }
        })
        .catch(error => {
          this.isLoading = false
          this.notifyError(error)
        })
    },
    changeQuantity (quantity) {
      if (!this.isAllowCartManagement) {
        this.$emit('changeQuantity', quantity)
      } else {
        if (this.isLoading || this.modificator) return
        this.isLoading = true
        this.CHANGE_PRODUCT_QUANTITY({
          productHashId: this.product.hashId,
          productQuantity: quantity,
          storeUid: this.storeUidForCheckout,
          deliveryType: this.formData.deliveryType ? this.formData.deliveryType.methodId : null,
          bonuses: this.bonusValue
        })
          .then(() => {
            this.isLoading = false
            this.CHANGE_CART_RTB_HOUSE_CODE_FLAG(true)
            this.$emit('changedCart')
            if (this.showProductDeliveryStatus) {
              if (this.useBonuses) {
                this.sendBonusesToABM()
              }
              if (this.deliveryCity.id) {
                this.FETCH_DELIVERY_TYPES()
              }
            }
          })
          .catch(error => {
            this.isLoading = false
            this.notifyError(error)
          })
      }
    },
    notifyError (message) {
      this.$notify({
        group: 'error',
        title: this.$t('error'),
        text: message,
        type: 'error'
      })
    }
  },
  filters: { CurrencyFormatter, Pluralize }
}
</script>

<style scoped lang="sass">

.cart-item-wrapper
  position: relative

.cart-item
  position: relative
  display: flex
  flex-flow: row nowrap
  align-items: flex-start
  width: 100%
  padding-bottom: 55px

  +mmin($tablet--hor)
    padding-bottom: 0
  &__delivery-status
    display: inline-block
    padding: 1px 10px
    border-radius: 3px
    background: #F8F8F8
    color: #333333
    font-size: 14px
    margin-top: 8px

.cart-item__remove-wrapper
  margin-right: 10px

  +mmin($tablet--hor)
    margin-right: 15px

.cart-item__img-wrapper
  display: flex
  align-items: center
  justify-content: center
  width: 65px
  height: 65px
  overflow: hidden
  flex-shrink: 0

  +mmin($tablet--hor)
    width: 100px
    height: 100px

.cart-item__img
  display: block
  width: 100%
  object-fit: contain
  font-size: 6px

.cart-item__left-wrapper
  display: flex
  flex-flow: row nowrap
  flex-shrink: 0
  align-items: center

.cart-item__right-wrapper
  width: 100%
  margin-left: 20px

.cart-item__title
  font-size: 14px

.cart-item__status
  display: block
  font-size: 14px
  font-weight: 500
  margin-top: 10px

  &--not-in-stock
    color: $placeholder-color

  &--will-be-in-stock
    color: $main-color

  &--in-stock
    color: $nd-green-text-color

.cart-item__add-to-cart
  margin-top: 10px

.cart-item__bonuses-wrapper
  display: flex
  flex-flow: row nowrap
  align-items: center
  width: max-content
  padding: 5px
  background-color: #f8f8f8
  border-radius: 3px
  margin-top: 10px

  +mmin($tablet--hor)
    padding: 8px 10px

.cart-item__bonuses-icon
  width: 15px
  height: 15px
  object-fit: contain

  +mmin($tablet--hor)
    width: 20px
    height: 20px

.cart-item__bonuses-text
  font-size: 14px
  color: rgba(51, 51, 51, 0.7)
  margin-left: 7px

.cart-item__actions-wrapper
  position: absolute
  bottom: 0
  left: 0
  display: flex
  flex-flow: row nowrap
  align-items: center
  justify-content: space-between
  width: 100%

  +mmin($tablet--hor)
    position: static
    bottom: auto
    left: auto
    padding-top: 15px

.cart-item__price-wrapper,
.cart-item__total-price-wrapper
  display: flex
  flex-flow: column
  width: 100px
  flex-shrink: 0

.cart-item__total-price-wrapper
  align-items: flex-end

.cart-item__price,
.cart-item__total-price
  font-size: 15px

  +mmin($tablet--hor)
    font-size: 16px

.cart-item__price

  &--old
    text-decoration-color: $main-color
    color: $placeholder-color

  &--contrast
    color: $main-color
    font-weight: 500

.cart-item__total-price
  font-weight: 500

  &--contrast
    color: $main-color

.cart-inner-items
  position: relative
  display: block
  border: 1px solid $nd-border-color
  border-radius: 3px
  margin-top: 10px
  padding: 15px 20px 10px

  // изначально в макете был отступ
  //+mmin($tablet--hor)
  //  margin-left: 155px

.cart-inner-items__title
  position: absolute
  top: -9px
  left: 10px
  padding: 0 10px
  background-color: #fff
  color: $placeholder-color
  font-size: 14px

.cart-inner-items__list
  +list-unstyled

.cart-inner-items__item
  display: flex
  flex-flow: row nowrap
  align-items: center

  :not(:last-child)
    margin-bottom: 10px

.cart-inner-items__item-img-wrapper
  display: flex
  align-items: center
  justify-content: center
  width: 50px
  height: 50px
  overflow: hidden
  flex-shrink: 0

  +mmin($tablet--hor)
    width: 75px
    height: 75px

.cart-inner-items__item-img
  display: block
  width: 100%
  object-fit: contain
  font-size: 6px

.cart-inner-items__item-title
  font-size: 14px
  margin-left: 20px

.cart-item--small
  padding-bottom: 55px

  .cart-item__remove-wrapper
    margin-right: 10px

  .cart-item__img-wrapper
    width: 65px
    height: 65px

  .cart-item__bonuses-wrapper
    padding: 5px

  .cart-item__bonuses-icon
    width: 15px
    height: 15px

  .cart-item__actions-wrapper
    position: absolute
    bottom: 0
    left: 0

  .cart-inner-items
    margin-left: 0

  .cart-inner-items__item-img-wrapper
    width: 50px
    height: 50px
</style>
